/**
 * The 20 locked v3 modules (SOLUTION-BIBLE §4).
 *
 * Each entry drives:
 *   - ModuleRail tab rendering (icon + label + role gating)
 *   - React Router route registration
 *   - Module landing page title + scope line
 *
 * Icons are from lucide-react (local npm package, never CDN).
 */

import type { ModuleCode, UserRole } from '@retail-ops/shared-types/enums';
import type { LucideIcon } from 'lucide-react';
import {
  Home,
  Users,
  FilePlus,
  Wrench,
  UserCog,
  PiggyBank,
  CalendarClock,
  Archive,
  CheckSquare,
  PenLine,
  ShieldCheck,
  FolderOpen,
  Skull,
  BadgeCheck,
  FileSearch,
  CalendarPlus,
  Gauge,
  LineChart,
  BellRing,
  Globe,
  LifeBuoy,
} from 'lucide-react';

export interface ModuleDefinition {
  code: ModuleCode;
  name: string;
  scope: string;
  path: string;
  icon: LucideIcon;
  /** Roles that see the module rail tab for this module. */
  roles: UserRole[];
  /**
   * If true the module tab is always hidden from the rail
   * (e.g. RCP is placeholder only — built in RCP-portal app, not here).
   */
  hiddenFromRail?: boolean;
  /**
   * When the tab's `path` is a deep link (New Acct -> /rao/new), the prefix
   * that should keep the tab highlighted (/rao). Defaults to `path`.
   */
  activePrefix?: string;
  /** If true, shown as a disabled tab when the current role can't access (per dashboard-officer mockup). */
  disabledForNonAuthorized?: boolean;
}

/**
 * Rail-visible modules in Bill's 2026-04-23 order. Admin dropdown appends
 * automatically via ShellLayout.buildAdminSubmenu after the last rail item.
 *
 * Bill's role-gate spec (FUTURE: moves to SQL Navigation_Tabs catalog):
 *   Home        - Officer, Admin
 *   Lobby       - Officer, Supervisor, Manager, Executive, Admin
 *   Tasks       - Officer, Supervisor, Manager, Admin
 *   Approvals   - Supervisor, Manager, Admin
 *   Metrics     - Manager, Executive, Admin
 *   New Acct    - Officer, Admin
 *   Maint       - Officer, Admin
 *   Signing     - Officer, Supervisor, Admin
 *   Compliance  - Officer, Supervisor, Manager, Admin
 *   Documents   - Officer, Supervisor, Manager, Admin
 *   Appts       - Officer, Admin
 *
 * Role-name mapping (Wave 1 G-7 redesign — Bill 2026-04-27):
 *   Officer    = retail officer (collapses legacy FSR + LoanOfficer)
 *   Supervisor = Group-scoped (UserGroups membership)
 *   Manager    = Region-scoped
 *   Executive  = Region-scoped, no cart visibility (reports only)
 *   Admin      = business-config administrator. Sees everything.
 *   SuperAdmin = infrastructure administrator. Supersedes Admin.
 *
 * SystemOps + Risk are panel-only roles (apps/sysops-panel + apps/
 * risk-mgmt-panel) and intentionally do NOT appear in any ROP-Teams
 * module roles array. Legacy Compliance/Auditor users were migrated to
 * Risk; legacy Kiosk is now an Azure SPA; Customer is a customerType,
 * not a user role.
 */
export const MODULES: ModuleDefinition[] = [
  {
    code: 'ROP',
    name: 'Home',
    scope: 'Officer landing — topbar, module rail, role-based defaults, context-resume.',
    // Bill 2026-07-13 Items 1/5: the rail Home now points at the HomeLanding
    // ('/') — the old /rop OfficerDashboard (raw event feed, wonky KPIs) is
    // retired; /rop redirects in App.tsx.
    path: '/',
    icon: Home,
    // PILOT 2026-09-11 (Bill, live smoke test): Supervisors/Managers had NO Home
    // tab - once inside New Acct there was no way back without a reload. Home
    // is for everyone.
    roles: ['Officer', 'Supervisor', 'Manager', 'Executive', 'Admin', 'SuperAdmin'],
  },
  {
    code: 'RLQ',
    name: 'Lobby',
    scope: 'Kiosk + officer check-in; provisional entries for new customers; service-type routing.',
    path: '/rlq',
    icon: Users,
    roles: ['Officer', 'Supervisor', 'Manager', 'Executive', 'Admin', 'SuperAdmin'],
    // Bill 2026-07-13 Item 3: hidden until fully fleshed out for pilot.
    hiddenFromRail: true,
  },
  {
    code: 'RTM',
    name: 'Tasks',
    scope: 'Module-owned authoring, RTM aggregates; hybrid routing; Supervisor override.',
    path: '/rtm',
    icon: CheckSquare,
    roles: ['Officer', 'Supervisor', 'Manager', 'Admin', 'SuperAdmin'],
    // PILOT 2026-09-04: hidden from the rail AND the Ctrl+K palette for the
    // FSR pilot - not wired end to end for Phase 1 (Bill: nothing unwired in
    // front of pilot users). Restore by deleting this line.
    hiddenFromRail: true,
  },
  {
    code: 'RAP',
    name: 'Approvals',
    scope: 'Config + reporting on top of native Microsoft Teams Approvals (no custom UI).',
    path: '/rap',
    icon: BadgeCheck,
    roles: ['Supervisor', 'Manager', 'Admin', 'SuperAdmin'],
    // PILOT 2026-09-11: hidden. The Graph Approvals integration is not live —
    // the tenant has not granted Approval.ReadWrite, so Pending/History render
    // a MOCK dataset (Maria Sanchez, Tom Reilly, Sandra Park) behind a banner,
    // and the History tab crashes outright: TypeError "Cannot read properties
    // of undefined (reading 'join')". Fabricated approvals with live-looking
    // Approve/Reject buttons in front of an FSR is worse than no tab at all.
    // Restore by deleting this line once consent is granted.
    hiddenFromRail: true,
  },
  {
    code: 'RPM',
    name: 'Metrics',
    scope: 'Executive/Manager daily ops dashboard — KPI strip + widget grid + right rail.',
    path: '/rpm',
    icon: Gauge,
    roles: ['Manager', 'Executive', 'Admin', 'SuperAdmin'],
    // Bill 2026-07-13 Item 3: hidden until fully fleshed out for pilot.
    hiddenFromRail: true,
  },
  {
    code: 'RAO',
    name: 'New Acct',
    scope: 'People → Products → Ownership → Compliance → Review → Sign → Commit.',
    // Bill 2026-09-11: the tab lands on the New application dialog itself
    // (/rao/new bootstraps a cart and opens People) - not on the list of carts
    // and online carts that Home already shows, plus another click.
    path: '/rao/new',
    activePrefix: '/rao',
    icon: FilePlus,
    roles: ['Officer', 'Admin', 'SuperAdmin'],
  },
  {
    code: 'RAM',
    name: 'Maint',
    scope: 'Product-agnostic service-request pipeline; 3 entry points.',
    path: '/ram',
    icon: Wrench,
    roles: ['Officer', 'Admin', 'SuperAdmin'],
    // PILOT 2026-09-04: hidden from the rail AND the Ctrl+K palette for the
    // FSR pilot - not wired end to end for Phase 1 (Bill: nothing unwired in
    // front of pilot users). Restore by deleting this line.
    hiddenFromRail: true,
  },
  {
    code: 'RSC',
    name: 'Signing',
    scope: 'Multi-method signing (email, QR, embedded, sig pad, wet); correction flow.',
    path: '/rsc',
    icon: PenLine,
    roles: ['Officer', 'Supervisor', 'Admin', 'SuperAdmin'],
    // PILOT 2026-09-04: hidden from the rail AND the Ctrl+K palette for the
    // FSR pilot - not wired end to end for Phase 1 (Bill: nothing unwired in
    // front of pilot users). Restore by deleting this line.
    hiddenFromRail: true,
  },
  {
    code: 'RCC',
    name: 'Compliance',
    scope: 'Officer-first: OFAC triage, SAR, CTR, CIP exceptions, Adverse Action, Death, Escheatment.',
    path: '/rcc',
    icon: ShieldCheck,
    roles: ['Officer', 'Supervisor', 'Manager', 'Admin', 'SuperAdmin'],
    // PILOT 2026-09-04: hidden from the rail AND the Ctrl+K palette for the
    // FSR pilot - not wired end to end for Phase 1 (Bill: nothing unwired in
    // front of pilot users). Restore by deleting this line.
    hiddenFromRail: true,
  },
  {
    code: 'RDL',
    name: 'Documents',
    scope: 'ao_Documents library + delayed-indexing export.',
    path: '/rdl',
    icon: FolderOpen,
    roles: ['Officer', 'Supervisor', 'Manager', 'Admin', 'SuperAdmin'],
  },
  {
    code: 'RAE',
    name: 'Appts',
    scope: 'MS Bookings + Teams calendar + PCB-skinned 5-step flow; video via Teams.',
    path: '/rae',
    icon: CalendarPlus,
    roles: ['Officer', 'Admin', 'SuperAdmin'],
    // Bill 2026-07-13 Item 3: hidden until fully fleshed out for pilot.
    hiddenFromRail: true,
  },

  // ---- Acct Maintenance submenu children (hidden from rail; appear in Maint dropdown) ----
  {
    code: 'RCM',
    name: 'CIF Update',
    scope: 'Search-first; inline Related People; no dedup (SSN/EIN enforced at CustAdd).',
    path: '/rcm',
    icon: UserCog,
    roles: ['Officer', 'Supervisor', 'Manager', 'Admin', 'SuperAdmin'],
    // PILOT 2026-09-04: hidden from the rail AND the Ctrl+K palette for the
    // FSR pilot - not wired end to end for Phase 1 (Bill: nothing unwired in
    // front of pilot users). Restore by deleting this line.
    hiddenFromRail: true,
  },
  {
    code: 'RIM',
    name: 'IRA',
    scope: 'Core SOR; 15 IRA features; native PDF templates replace Ascensus paper.',
    path: '/rim',
    icon: PiggyBank,
    roles: ['Officer', 'Supervisor', 'Manager', 'Admin', 'SuperAdmin'],
    // PILOT 2026-09-04: hidden from the rail AND the Ctrl+K palette for the
    // FSR pilot - not wired end to end for Phase 1 (Bill: nothing unwired in
    // front of pilot users). Restore by deleting this line.
    hiddenFromRail: true,
  },
  {
    code: 'RCD',
    name: 'CD',
    scope: 'Non-IRA CDs; Core auto-renew default; Officer waiver authority per reason codes.',
    path: '/rcd',
    icon: CalendarClock,
    roles: ['Officer', 'Supervisor', 'Manager', 'Admin', 'SuperAdmin'],
    // PILOT 2026-09-04: hidden from the rail AND the Ctrl+K palette for the
    // FSR pilot - not wired end to end for Phase 1 (Bill: nothing unwired in
    // front of pilot users). Restore by deleting this line.
    hiddenFromRail: true,
  },
  {
    code: 'RSM',
    name: 'SDB',
    scope: 'Box lookup, vacant, past-due, access log, close-out wizard, escheatment handoff.',
    path: '/rsm',
    icon: Archive,
    roles: ['Officer', 'Supervisor', 'Manager', 'Admin', 'SuperAdmin'],
    // PILOT 2026-09-04: hidden from the rail AND the Ctrl+K palette for the
    // FSR pilot - not wired end to end for Phase 1 (Bill: nothing unwired in
    // front of pilot users). Restore by deleting this line.
    hiddenFromRail: true,
  },

  // ---- Admin dropdown children (hidden from rail; appear in Admin dropdown) ----
  // Admin-role visibility: business-config administration
  //   - Workflow gating, user-role provisioning, lookups maintenance,
  //     disclosures edit, reporting, audit, notifications.
  // SuperAdmin-role visibility: infrastructure administration — supersedes Admin.
  {
    code: 'RKL',
    name: 'Kill List',
    scope: 'Auto-populate from archival; Test Mode auto-verified; two-step verify+execute.',
    path: '/rkl',
    icon: Skull,
    roles: ['Supervisor', 'Admin', 'SuperAdmin'],
    hiddenFromRail: true,
  },
  {
    code: 'RAS',
    name: 'Audit',
    scope: 'Internal audit report generator; 14 templates + ad-hoc builder; AICPA sampling.',
    path: '/ras',
    icon: FileSearch,
    roles: ['Admin', 'SuperAdmin'],
    hiddenFromRail: true,
  },
  {
    code: 'RRM',
    name: 'Reporting',
    scope: 'Executive reporting — CEO/CFO/Bill; Weekly/monthly/quarterly; Innovation tab.',
    path: '/rrm',
    icon: LineChart,
    roles: ['Executive', 'Admin', 'SuperAdmin'],
    // Housed under the Admin dropdown.
    hiddenFromRail: true,
  },
  {
    code: 'RNC',
    name: 'Notif',
    scope: 'Outbound spine — Email/Teams/SMS/Physical; templates in SQL; 7-yr delivery log.',
    path: '/rnc',
    icon: BellRing,
    roles: ['Admin', 'SuperAdmin'],
    // Housed under the Admin dropdown.
    hiddenFromRail: true,
  },
  {
    code: 'HLP',
    name: 'Help',
    scope: 'Pilot orientation — getting started, how-do-I, troubleshooting, and About (identity, build, live service status).',
    path: '/help',
    icon: LifeBuoy,
    // Bill 2026-09-11: Help lives in the HEADER - the ? next to Notifications
    // and the user badge, where every app keeps it - not as a module tab. The
    // route stays so /help deep-links and the footer/About still resolve.
    hiddenFromRail: true,
    // Everyone. A pilot user who cannot find help has no other route to it.
    roles: ['Officer', 'Supervisor', 'Manager', 'Executive', 'Admin', 'SuperAdmin'],
  },
  {
    code: 'RCP',
    name: 'Portal',
    scope: 'Placeholder — customer-facing Static Web App lives in apps/RCP-portal (Wave 3).',
    path: '/rcp',
    icon: Globe,
    roles: ['SuperAdmin'],
    hiddenFromRail: true,
  },
];

/** Module lookup by code. */
export const MODULE_BY_CODE: Record<ModuleCode, ModuleDefinition> = Object.fromEntries(
  MODULES.map((m) => [m.code, m]),
) as Record<ModuleCode, ModuleDefinition>;

/** Case-insensitive role membership — dbo.UserRoleAssignments rows arrive in
 * mixed casing ('Officer' vs 'OFFICER' vs 'ADMIN'); a case-sensitive match
 * silently blanked Peter's entire ribbon (Bill 2026-07-23). */
/**
 * A module held back for the pilot (hiddenFromRail). Its in-page entry points and its route stay off too, not
 * just the rail (Bill 2026-10-17: "Account Maintenance is not approved, in scope or ready for Phase 1 Pilot").
 */
export function isModuleHidden(code: ModuleCode): boolean {
  return MODULE_BY_CODE[code]?.hiddenFromRail === true;
}

export function roleIncludes(roles: readonly string[], role: string): boolean {
  const target = role.toLowerCase();
  return roles.some((r) => r.toLowerCase() === target);
}

/** Modules visible to a person holding ANY of these roles (not hidden from rail). The rail used to follow the single
 *  primary role, so an Executive who is also an Officer lost New Acct (Officer/Admin/SuperAdmin only) - 2026-10-07. */
export function modulesForRoles(roles: readonly string[]): ModuleDefinition[] {
  return MODULES.filter((m) => !m.hiddenFromRail && roles.some((r) => roleIncludes(m.roles, r)));
}

/** Filter modules visible to the given role (and not hidden from rail). */
export function modulesForRole(role: UserRole): ModuleDefinition[] {
  return MODULES.filter((m) => !m.hiddenFromRail && roleIncludes(m.roles, role));
}
